import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Free gifts added with every subscription (Bamboo Jar, E-Book).
// Appstle's "Subscription free product" discount makes them $0 at checkout.
const GIFT_VARIANTS = [55767177363798, 55767299195222];
const GIFT_PROP = '_voora_gift';

const root = document.querySelector('[data-vr-pdp]');
const form = root?.querySelector('form.vr-pdp__form');
const routes = () => window.Theme?.routes || {};
const cartUrl = () => (routes().cart_url || '/cart');

async function getCart() {
  return fetch(cartUrl() + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
}

function sectionIds() {
  return [...new Set([...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean))];
}

function readSelection() {
  const bundle = root.querySelector('input[name=vr_bundle]:checked') || root.querySelector('[data-vr-default]');
  const planRadio = root.querySelector('input[name=vr_plan]:checked');
  const subscribe = !!(planRadio && planRadio.dataset.sub);
  const variantId = Number(form.querySelector('input[name=id]')?.value);
  const quantity = Number(bundle?.value || bundle?.dataset.qty) || 1;
  const sellingPlan = subscribe ? Number(bundle?.dataset.plan) || null : null;
  return { variantId, quantity, subscribe, sellingPlan };
}

/** Remove gift lines when the cart no longer contains a subscription, or when they are not free. */
async function cleanGifts(cart, { requireFree = false } = {}) {
  const hasSub = cart.items.some((i) => i.selling_plan_allocation);
  const updates = {};
  for (const item of cart.items) {
    const isGift = GIFT_VARIANTS.includes(item.variant_id) && item.properties && item.properties[GIFT_PROP];
    if (!isGift) continue;
    if (!hasSub || (requireFree && item.final_line_price > 0)) updates[item.key] = 0;
    else if (item.quantity > 1) updates[item.key] = 1;
  }
  if (!Object.keys(updates).length) return null;
  const body = { updates };
  const ids = sectionIds();
  if (ids.length) { body.sections = ids.join(','); body.sections_url = window.location.pathname; }
  const res = await fetch(routes().cart_update_url ? routes().cart_update_url + '.js' : '/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());
  return res;
}

function announce(target, cart, sections, source) {
  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
  deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections, source, didError: false } });
}

if (form && !form.dataset.vrAjax) {
  form.dataset.vrAjax = '1';
  // Capture phase on window: runs before other scripts that intercept product form submits.
  window.addEventListener('submit', async (event) => {
    if (event.target !== form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = form.querySelector('.vr-atc');
    if (button?.disabled) return;
    if (button) { button.disabled = true; button.classList.add('is-loading'); }

    const { variantId, quantity, subscribe, sellingPlan } = readSelection();
    const items = [sellingPlan ? { id: variantId, quantity, selling_plan: sellingPlan } : { id: variantId, quantity }];

    if (sellingPlan) {
      const current = await getCart().catch(() => ({ items: [] }));
      for (const gid of GIFT_VARIANTS) {
        if (!current.items.some((i) => i.variant_id === gid)) items.push({ id: gid, quantity: 1, properties: { [GIFT_PROP]: 'Free with subscription' } });
      }
    }

    const body = { items };
    const ids = sectionIds();
    if (ids.length) { body.sections = ids.join(','); body.sections_url = window.location.pathname; }

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(routes().cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      let cart = await getCart();
      let sections = data.sections;

      if (data.status) {
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'voora-product' } });
        showError(data.description || data.message || 'Could not add to cart');
        return;
      }

      // Safety: never let a gift be charged. If the discount didn't make it free, remove it.
      if (sellingPlan) {
        const cleaned = await cleanGifts(cart, { requireFree: true });
        if (cleaned) {
          console.warn('[voora] Gift lines were not free and have been removed');
          cart = await getCart();
          sections = cleaned.sections || sections;
        }
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections, itemCount: quantity, source: 'voora-product', didError: false },
      });

      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        requestAnimationFrame(() => { if (!drawer.isOpen) drawer.open(); });
      } else {
        window.location.href = cartUrl();
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      showError('Something went wrong. Please try again.');
    } finally {
      if (button) { button.disabled = false; button.classList.remove('is-loading'); }
    }
  }, true);
}

// When the cart changes anywhere (e.g. subscription removed in the drawer), drop gifts that no longer qualify.
let cleaning = false;
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (cleaning || event.detail?.source === 'voora-gifts') return;
  event.promise?.then(async () => {
    if (cleaning) return;
    cleaning = true;
    try {
      const cart = await getCart();
      const res = await cleanGifts(cart);
      if (res) announce(document.getElementById('cart-drawer') || document, await getCart(), res.sections, 'voora-gifts');
    } catch (e) { console.warn(e); } finally { cleaning = false; }
  }).catch(() => {});
});

function showError(message) {
  let box = form.querySelector('.vr-atc-error');
  if (!box) {
    box = document.createElement('p');
    box.className = 'vr-atc-error';
    box.setAttribute('role', 'alert');
    form.querySelector('.vr-atc')?.insertAdjacentElement('afterend', box);
  }
  box.textContent = message;
  setTimeout(() => box && box.remove(), 8000);
}
